import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="legal-section">
      <h2>Privacy Policy</h2>
      <p>Last updated: July 26th, 2025</p>

      <h3>1. Introduction</h3>
      <p>
        SocratesLab ("we," "our," or "us") is committed to protecting the
        privacy and security of the personal information we collect. This
        Privacy Policy outlines how we collect, use, disclose, store, and
        protect your personal information when you use our website, Learning
        Management System ("LMS"), and related services (collectively, the
        "Services").
      </p>
      <p>
        As an education technology startup based in Quebec serving university
        communities, we comply with Quebec's Law 25 (Act to Modernize
        Legislative Provisions Respecting the Protection of Personal
        Information) and PIPEDA (Personal Information Protection and Electronic
        Documents Act). Our platform is designed for university faculty and
        students (18+), and we continually strive to enhance our Services and
        ensure the highest standards of privacy and security for our users.
      </p>

      <h3>2. Consent and Your Rights</h3>
      <p>
        We collect and use your personal information only with your explicit
        consent. You have the following rights:
      </p>
      <ul>
        <li>Right to access your personal information</li>
        <li>Right to rectify inaccurate information</li>
        <li>Right to withdraw consent at any time</li>
        <li>
          Right to data portability (receive your data in a structured format)
        </li>
        <li>Right to be forgotten (request deletion of your data)</li>
        <li>Right to de-indexation (request removal from search results)</li>
      </ul>
      <p>
        To exercise these rights, contact our Privacy Officer at the address
        provided below. We will respond to your request within 30 days.
      </p>

      <h4>Contact Information for Privacy Officer</h4>
      <p>
        <strong>[Privacy Officer]</strong>
        <br />
        <strong>[privacy@socrateslab.ca]</strong>
      </p>

      <h3>3. Data Retention and Destruction</h3>
      <p>
        We retain your personal information only for as long as necessary to
        fulfill the purposes for which it was collected, or as required by law.
        Specifically:
      </p>
      <ul>
        <li>
          Account information: Retained while your account is active plus 2
          years after closure
        </li>
        <li>
          Learning activity data: Retained for 3 years from date of creation
        </li>
        <li>
          Communication records: Retained for 2 years from last interaction
        </li>
        <li>Technical logs: Retained for 6 months</li>
      </ul>
      <p>
        After these periods, your data will be securely destroyed or anonymized.
      </p>

      <h3>4. Automated Decision-Making and AI Features</h3>
      <p>
        <strong>AI Features for Plus Users:</strong> Our AI-powered features are
        available exclusively to users with "Plus" subscriptions and include:
      </p>
      <ul>
        <li>AI tutoring with personalized educational conversations</li>
        <li>Automated feedback on text and audio responses</li>
        <li>Audio transcription and pronunciation analysis</li>
        <li>Learning content categorization and generation</li>
        <li>Progress assessment and learning path recommendations</li>
      </ul>
      <p>
        <strong>AI Opt-out:</strong> You can opt-out of all AI features by
        choosing or downgrading to a non-Plus account tier. All core educational
        functionality remains available without AI features.
      </p>
      <p>
        <strong>Human Oversight:</strong> All AI-generated assessments and
        feedback are subject to teacher review and can be overridden. You have
        the right to:
      </p>
      <ul>
        <li>Be informed when decisions about you are made automatically</li>
        <li>Request human intervention in automated decisions</li>
        <li>Express your point of view about automated decisions</li>
        <li>Contest automated decisions through your instructor</li>
        <li>Access non-AI alternatives for all educational activities</li>
      </ul>

      <h3>5. Cross-Border Data Transfers</h3>
      <p>
        <strong>Data Storage in Canada:</strong> Your primary personal
        information (profiles, grades, course enrollment, and permanent records)
        is stored in Canada using Google Firebase servers in the
        northamerica-northeast1 region.
      </p>
      <p>
        <strong>AI Processing in the United States:</strong> When you use AI
        features (available to Plus users only), your educational content may be
        processed by OpenAI, located in the United States, for the following
        purposes:
      </p>
      <ul>
        <li>Generating personalized educational feedback</li>
        <li>Creating AI tutor responses</li>
        <li>Categorizing learning materials</li>
      </ul>
      <p>
        <strong>Safeguards for Cross-Border Transfers:</strong>
      </p>
      <ul>
        <li>
          We obtain your explicit consent for AI features using international
          services
        </li>
        <li>We ensure adequate data protection through contractual measures</li>
        <li>We maintain transparency about data processing locations</li>
        <li>We ensure compliance with Quebec and Canadian privacy laws</li>
        <li>
          Only AI-related content is processed internationally; all other data
          remains in Canada
        </li>
      </ul>

      <h3>6. Security Incidents and Breach Notification</h3>
      <p>In case of a security incident affecting your personal information:</p>
      <ul>
        <li>We will notify you promptly if there is a risk of serious harm</li>
        <li>We will notify the Commission d'accès à l'information du Québec</li>
        <li>We will provide details about the incident and our response</li>
        <li>
          We will implement measures to reduce the risk of future incidents
        </li>
      </ul>

      <h3>7. Who We Are</h3>
      <p>
        SocratesLab
        <br />
        Address: 260 rue France, Saint-Philippe, Quebec, Canada, J0L 2K0
        <br />
        Email: info@socrateslab.ca
        <br />
        Phone: 514-792-7385
      </p>

      <h3>8. Scope of This Policy</h3>
      <p>
        This Privacy Policy applies to personal information that we collect when
        you:
      </p>
      <ul>
        <li>Access or use our Services</li>
        <li>Contact us</li>
        <li>Engage with us in any other manner</li>
      </ul>
      <p>
        This Policy does not apply to information collected by any third-party
        sites, services, or organizations that we may link to or reference.
        These third parties have their own privacy policies, which we encourage
        you to review.
      </p>

      <h3>9. Definitions</h3>
      <ul>
        <li>
          Personal Information: Any information about an identifiable
          individual. This may include your name, email address, and other
          information that can be used to identify you.
        </li>
        <li>
          Non-Personal Information: Information that cannot, by itself, be used
          to identify an individual. This may include aggregated or anonymized
          data, browser type, and general usage statistics.
        </li>
      </ul>

      <h3>10. Collection of Personal Information</h3>
      <h4>10.1 Information You Provide Directly</h4>
      <p>
        <strong>Required Information:</strong>
      </p>
      <ul>
        <li>
          Account Registration (Required for core functionality):
          <ul>
            <li>First name and last name - Required for user identification</li>
            <li>
              Email address - Required for authentication and communications
            </li>
            <li>Password - Required for account security</li>
          </ul>
        </li>
        <li>
          Course Participation (Required if enrolled):
          <ul>
            <li>Assignment submissions - Required for course completion</li>
            <li>Quiz responses - Required for assessment</li>
            <li>Progress tracking - Required for course management</li>
          </ul>
        </li>
      </ul>

      <p>
        <strong>Optional Information:</strong>
      </p>
      <ul>
        <li>Avatar selection - Optional for profile personalization</li>
        <li>Communication preferences - Optional for notifications</li>
        <li>Learning preferences - Optional for content personalization</li>
      </ul>

      <h4>10.2 Information Collected Automatically</h4>
      <p>
        <strong>Collection Methods:</strong>
      </p>
      <ul>
        <li>
          Direct Collection:
          <ul>
            <li>Web forms for registration and profile updates</li>
            <li>File uploads for assignments</li>
            <li>Text input for communications</li>
          </ul>
        </li>
        <li>
          Automated Collection:
          <ul>
            <li>Login timestamps via Firebase Authentication</li>
            <li>Progress tracking via learning management system</li>
            <li>Error logging for system maintenance</li>
          </ul>
        </li>
        <li>
          Third-party Collection:
          <ul>
            <li>Microsoft Single Sign-On (when enabled)</li>
            <li>Google Firebase for authentication and storage</li>
          </ul>
        </li>
      </ul>

      <h4>10.3 Data Access Requests</h4>
      <p>To request access to your personal information:</p>
      <ol>
        <li>
          Send an email to privacy@socrateslab.ca with the subject line
          "Personal Data Access Request"
        </li>
        <li>
          Include in your email:
          <ul>
            <li>Your full name</li>
            <li>Email address associated with your account</li>
            <li>A copy of government-issued ID for verification</li>
          </ul>
        </li>
        <li>
          Timeline and Process:
          <ul>
            <li>We will respond within 30 days</li>
            <li>
              Your data will be provided in a readable format (PDF or
              spreadsheet)
            </li>
            <li>
              Complex requests may require a 30-day extension (we will notify
              you)
            </li>
          </ul>
        </li>
      </ol>

      <h4>10.4 Service Limitations</h4>
      <p>If you choose not to provide required information:</p>
      <ul>
        <li>Without an email and password, you cannot create an account</li>
        <li>Without a name, you cannot participate in courses</li>
        <li>Without assignment submissions, you cannot complete courses</li>
      </ul>
      <p>
        Optional information will not affect core functionality but may limit
        personalization features.
      </p>

      <h3>11. Use of Personal Information</h3>
      <p>We use personal information to:</p>
      <ul>
        <li>
          Provide and Improve the Services: Operate, maintain, and enhance our
          LMS and related offerings, including AI-based features and user
          experiences.
        </li>
        <li>
          Communicate with You: Send service-related messages, newsletters, or
          other communications if you contact us or subscribe to updates.
        </li>
        <li>
          Research and Development: Use aggregated or anonymized information to
          understand user needs and improve our platform's functionality,
          content, and performance.
        </li>
      </ul>

      <h3>12. Data Security</h3>
      <p>
        We implement reasonable administrative, technical, and physical
        safeguards to protect your personal information. This includes using
        Firebase Firestore for secure data storage, Firebase Authentication for
        verifying user identities, and Firestore Security Rules to enforce
        role-based access control.
      </p>

      <h3>13. Changes to This Policy</h3>
      <p>
        We will notify you of any material changes to this Privacy Policy
        through:
      </p>
      <ul>
        <li>Email notification to your registered email address</li>
        <li>Prominent notice on our website</li>
        <li>Request for renewed consent when necessary</li>
      </ul>
      <p>
        You have the right to withdraw your consent if you disagree with the
        changes.
      </p>

      <h3>14. Contact Information</h3>
      <p>
        For privacy-related inquiries or to exercise your rights, contact our
        Privacy Officer:
        <br />
        SocratesLab
        <br />
        Attn: Privacy Officer
        <br />
        260 rue France
        <br />
        Saint-Philippe, Quebec, Canada, J0L 2K0
        <br />
        Email: privacy@socrateslab.ca
        <br />
        Phone: 514-792-7385
      </p>
      <p>
        You also have the right to file a complaint with:
        <br />
        Commission d'accès à l'information du Québec
        <br />
        Website: www.cai.gouv.qc.ca
      </p>
    </div>
  );
};

export default PrivacyPolicy;
